import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { InstagramAccount } from '../../types';
import { translations } from '../../translations';
import { FollowerLineChart } from '../../components/Overview/FollowerLineChart';
import { FollowerGainsChart } from '../../components/Overview/FollowerGainsChart';

interface OverviewProps {
  account: InstagramAccount;
  isDarkMode: boolean;
  language: string;
}

export function Overview({ account, isDarkMode, language }: OverviewProps) {
  const t = translations[language as keyof typeof translations];
  const [currentWeek, setCurrentWeek] = useState(1);
  const [weekProgress, setWeekProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    if (account.created_at) {
      const creationDate = new Date(account.created_at);
      const today = new Date();
      
      // Calculer la différence en jours de manière plus précise
      const creationDateOnly = new Date(creationDate.getFullYear(), creationDate.getMonth(), creationDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const diffTime = todayOnly.getTime() - creationDateOnly.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Calculer le numéro de semaine (1-4) et masquer après 28 jours (4 semaines complètes)
      const weekNumber = Math.floor(diffDays / 7) + 1;
      const daysInCurrentWeek = diffDays % 7 || 7;
      const progressPercentage = Math.min(100, (daysInCurrentWeek / 7) * 100);
      
      // Masquer la barre après 28 jours (4 semaines complètes)
      const shouldShowProgress = diffDays < 28;
      
      console.log('Account:', account.username, 'Days since creation:', diffDays, 'Week number:', weekNumber, 'Show progress:', shouldShowProgress);
      
      setCurrentWeek(weekNumber);
      setWeekProgress(shouldShowProgress ? progressPercentage : 100);
      setShowProgress(shouldShowProgress);
    }
  }, [account.created_at]);

  const groupedHistory = React.useMemo(() => {
    if (!account.follower_history?.length) return [];
    
    const grouped = account.follower_history.reduce((acc, entry) => {
      const date = entry.date.split('T')[0];
      acc[date] = entry;
      return acc;
    }, {} as Record<string, typeof account.follower_history[0]>);
    
    return Object.values(grouped).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [account.follower_history]);

  return (
    <>
      {/* Connection Warning */}
      {account.connection_status && ['failed', '2fa'].includes(account.connection_status) && (
        <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-amber-500 flex-shrink-0\" size={32} />
            <p className="text-amber-700 text-sm">
              {t.connection_required}
            </p>
          </div>
        </div>
      )}

      {showProgress && (
        <div className={`p-6 rounded-lg border mb-6 ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <h3 className={`text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{t.amplr_ai_progress}</h3>
          
          <p className={`text-sm mb-4 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {t.amplr_ai_description}
          </p>

          <div className="flex gap-2">
            {[1, 2, 3, 4].map(week => (
              <div
                key={week}
                className={`flex-1 h-2 rounded-full overflow-hidden ${
                  isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
                }`}
              >
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${
                    week < currentWeek
                      ? 'bg-orange-500'
                      : week === currentWeek
                      ? 'bg-orange-500'
                      : 'bg-transparent'
                  }`}
                  style={{
                    width: week < currentWeek 
                      ? '100%' 
                      : week === currentWeek 
                      ? `${weekProgress}%`
                      : '0%'
                  }}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {[1, 2, 3, 4].map(week => (
              <div
                key={week}
                className={`text-xs ${
                  week <= currentWeek
                    ? isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
              >
                {t.week} {week}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className={`p-4 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className={`text-[13px] mb-1 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>{t.followers}</div>
          <div className={`text-xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{account.followers?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0'}</div>
        </div>
        <div className={`p-4 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className={`text-[13px] mb-1 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>{t.followers_gained}</div>
          <div className={`text-xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{account.follower_gained >= 0 ? '+' : ''}{account.follower_gained?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0'}</div>
        </div>
        <div className={`p-4 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className={`text-[13px] mb-1 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>{t.posts}</div>
          <div className={`text-xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{account.posts?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0'}</div>
        </div>
      </div>

      {/* Total Followers Chart */}
      <div className={`p-6 rounded-lg border mb-6 ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-lg font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{t.followers_evolution}</h2>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            account.follower_gained >= 0 
              ? 'text-emerald-600 bg-emerald-50' 
              : 'text-red-600 bg-red-50'
          }`}>
            <TrendingUp size={14} strokeWidth={2.5} />
            <span className="text-[13px] font-medium">
              {account.follower_gained >= 0 ? '+' : ''}{account.follower_gained?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0'}
            </span>
          </div>
        </div>

        {groupedHistory.length > 0 ? (
          <FollowerLineChart 
            groupedHistory={groupedHistory}
            isDarkMode={isDarkMode}
            language={language}
          />
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t.no_data_available}
            </p>
          </div>
        )}
      </div>

      {/* Daily Gains Chart */}
      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-lg font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{t.daily_gains}</h2>
        </div>

        {groupedHistory.length > 0 ? (
          <FollowerGainsChart
            groupedHistory={groupedHistory}
            isDarkMode={isDarkMode}
            language={language}
          />
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t.no_data_available}
            </p>
          </div>
        )}
      </div>
    </>
  );
}